//* Third Party Libraries
import React from "react";
import PropTypes from "prop-types";
import Link from "next/link";
import { useRouter } from "next/router";

//* Internal Libraries
import { useHeaderFlags } from "@lululemon/mwa-header-flags";
import { doesMatchRoute, prependI18nLocale, onlyText } from "./utilities";

/**
 * @name EwaLink
 * A component that conditionally renders a Next.js <Link> or a standard <a> element
 * based on route matching logic.
 * @param {Object} props - The props for the EwaLink component.
 * @param {string} props.href - The URL for the link. Required.
 * @param {Object} props.publicConfig - Configuration object required for route matching logic.
 * @param {React.ReactNode} [props.children] - The content displayed inside the link. Can be plain text or JSX nodes.
 * @param {Object|string} [props.analyticsAttrs] - Additional attributes for analytics purposes, passed directly to the rendered element.
 * @param {Object} [props.anchorProps] - Props specific to the `<a>` element.
 * Includes attributes like `className`, `target`, accessibility attributes (`aria-*`),
 * and event handlers (e.g., `onClick`, `onFocus`).
 * @param {Object} [props.dataProps] - Custom `data-*` attributes for testing or tracking.
 * These include attributes like `data-testid`, `data-lulu-track`, and others.
 * @returns {React.ReactElement|null} The rendered `<Link>` or `<a>` element, or `null` if no `href` is provided.
 * @example
 * <EwaLink
 *    href="/checkout"
 *    publicConfig={publicConfig}
 *    className="custom-link"
 *    analyticsAttrs={{ track: "checkout-click" }}
 *    anchorProps={{ target: "_blank" }}
 *    dataProps={{ "data-testid": "checkout-link" }}
 * >
 *   Go to Checkout
 * </EwaLink>
 */

const EwaLink = ({
  href,
  publicConfig,
  children,
  analyticsAttrs,
  anchorProps,
  dataProps,
  linkRef
}) => {
  const { locale } = useRouter() || {};
  const { frSubDomainMigrationHdrFlag } = useHeaderFlags();

  if (!href) {
    console.error("EwaLink: The 'href' prop is required but was not provided.");
    return null;
  }

  const { routeMatch, relativeURL } = doesMatchRoute(href, publicConfig);
  const finalAnchorProps = { ...anchorProps };
  if (
    !routeMatch &&
    !finalAnchorProps.rel &&
    onlyText(children).toLowerCase().includes("download")
  ) {
    finalAnchorProps.rel = "nofollow";
  }

  const spanForScreenReader = finalAnchorProps.target === "_blank" && (
    <span className="sr-only">(opens a new window)</span>
  );

  if (routeMatch) {
    return (
      <Link
        href={prependI18nLocale({
          locale,
          relativeURL,
          frSubDomainMigrationHdrFlag
        })}
      >
        <a
          ref={linkRef}
          {...finalAnchorProps}
          {...dataProps}
          {...analyticsAttrs}
        >
          {children}
          {spanForScreenReader}
        </a>
      </Link>
    );
  }

  return (
    <a
      ref={linkRef}
      {...finalAnchorProps}
      {...dataProps}
      {...analyticsAttrs}
      href={href}
    >
      {children}
      {spanForScreenReader}
    </a>
  );
};

EwaLink.propTypes = {
  href: PropTypes.string.isRequired,
  publicConfig: PropTypes.object.isRequired,
  children: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  analyticsAttrs: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  anchorProps: PropTypes.shape({
    className: PropTypes.string,
    target: PropTypes.string,
    "aria-label": PropTypes.string,
    "aria-disabled": PropTypes.bool,
    "aria-labelledby": PropTypes.string,
    "aria-hidden": PropTypes.bool,
    tabIndex: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    role: PropTypes.string,
    onClick: PropTypes.func,
    onKeyDown: PropTypes.func,
    onMouseDown: PropTypes.func,
    onMouseOver: PropTypes.func,
    onTouchStart: PropTypes.func,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    rel: PropTypes.string
  }),
  dataProps: PropTypes.shape({
    "data-component-link": PropTypes.string,
    "data-testid": PropTypes.string,
    "data-lulu-track": PropTypes.string,
    "data-testclass": PropTypes.string
  }),
  linkRef: PropTypes.oneOfType([PropTypes.object, PropTypes.func])
};

EwaLink.defaultProps = {
  children: null,
  analyticsAttrs: undefined,
  anchorProps: {
    className: undefined,
    target: undefined,
    "aria-label": undefined,
    "aria-disabled": undefined,
    "aria-labelledby": undefined,
    "aria-hidden": undefined,
    tabIndex: undefined,
    role: undefined,
    onClick: undefined,
    onKeyDown: undefined,
    onMouseDown: undefined,
    onMouseOver: undefined,
    onTouchStart: undefined,
    onFocus: undefined,
    onBlur: undefined,
    rel: undefined
  },
  dataProps: {
    "data-component-link": undefined,
    "data-testid": undefined,
    "data-lulu-track": undefined,
    "data-testclass": undefined
  },
  linkRef: undefined
};

export default EwaLink;
